import { AfterViewInit, Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { WbDataQueueAgents } from '../models/wb-base-item';
import { PbxWbCurrentModel } from '../../../api/models/pbx-wb-current-model';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { DateTime } from 'luxon';
import { PbxWbAgentModel } from '../../../api/models/pbx-wb-agent-model';
import { FlexModule } from '@angular/flex-layout';
import { NgIf, NgStyle, NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { DurationPipe } from '../../../core/pipes/duration.pipe';
import { SipContactPipe } from '../../../core/pipes/sip-contact.pipe';
import { SecondsToHhMmPipe } from '../../../core/pipes/seconds-to-hh-mm.pipe';
import { LocalPbxService } from '../../local-pbx.service';
import { NotificationService } from '../../../notification/notification.service';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

interface AgentsTableRow {
  agentAccount: string;
  agentName: string;
  activeCall: string;
  loggedIn: boolean;
  totalCalls: number;
  totalDuration: number;
  agentData: PbxWbAgentModel;
  totalLoggedIn: number;
  totalLoggedOut: number;
}

@Component({
  selector: 'app-pbx-wb-queue-agents-widget',
  template: `
    <div fxLayout="column" fxFlexFill>
      <div fxFlex="auto" fxFlexFill class="table-container">
        <mat-table
          [dataSource]="dataSource"
          [multiTemplateDataRows]="true"
          class="regular-table responsive"
          matSort
          matSortActive="agentName"
          matSortDirection="asc"
          matSortDisableClear
        >
          <ng-container matColumnDef="agentName">
            <mat-header-cell *matHeaderCellDef mat-sort-header="agentName" class="header-agent">Agent</mat-header-cell>
            <mat-cell *matCellDef="let row">
              <span class="mobile-label">Agent:</span>
              <button
                mat-icon-button
                [color]="row.loggedIn ? 'primary' : 'warn'"
                [ngStyle]="row.loggedIn ? { color: 'rgb(57, 203, 118)' } : {}"
                (click)="toggleAgentLogin(row)"
                [matTooltip]="row.loggedIn ? 'Logout agent' : 'Login agent'"
                [disabled]="!canToggleAgentLogin(row) || loadingAgentAccounts.has(row.agentAccount)"
              >
                <ng-container *ngIf="loadingAgentAccounts.has(row.agentAccount); else iconTpl">
                  <mat-progress-spinner
                    diameter="24"
                    mode="indeterminate"
                    [color]="row.loggedIn ? 'primary' : 'warn'"
                    [strokeWidth]="2"
                    style="vertical-align: middle;"
                  ></mat-progress-spinner>
                </ng-container>
                <ng-template #iconTpl>
                  <mat-icon>{{ row.loggedIn ? 'toggle_on' : 'toggle_off' }}</mat-icon>
                </ng-template>
              </button>
              <span class="row-text" style="margin-left: 5px">{{ row.agentName }}</span>
            </mat-cell>
          </ng-container>

          <ng-container matColumnDef="activeCall">
            <mat-header-cell *matHeaderCellDef mat-sort-header="activeCall">Live Calls</mat-header-cell>
            <mat-cell *matCellDef="let row">
              <span class="mobile-label">Live Calls:</span>
              <span class="row-text" style="white-space: pre;">{{ row.activeCall | sipContact }}</span>
            </mat-cell>
          </ng-container>

          <ng-container matColumnDef="answeredCalls">
            <mat-header-cell *matHeaderCellDef mat-sort-header="answeredCalls">Answered Calls</mat-header-cell>
            <mat-cell *matCellDef="let row">
              <span class="mobile-label">Answered Calls:</span>
              {{ row.answeredCalls }}
            </mat-cell>
          </ng-container>

          <ng-container matColumnDef="missedCalls">
            <mat-header-cell *matHeaderCellDef mat-sort-header="missedCalls">Missed Calls</mat-header-cell>
            <mat-cell *matCellDef="let row">
              <span class="mobile-label">Missed Calls:</span>
              {{ row.missedCalls }}
            </mat-cell>
          </ng-container>

          <ng-container matColumnDef="totalCalls">
            <mat-header-cell *matHeaderCellDef mat-sort-header="totalCalls">Total Calls</mat-header-cell>
            <mat-cell *matCellDef="let row">
              <span class="mobile-label">Total Calls:</span>
              {{ row.totalCalls }}
            </mat-cell>
          </ng-container>

          <ng-container matColumnDef="totalLoggedIn">
            <mat-header-cell *matHeaderCellDef mat-sort-header="totalLoggedIn">Total Logged In</mat-header-cell>
            <mat-cell *matCellDef="let row">
              <span class="mobile-label">Total Logged In:</span>
              {{ row.totalLoggedIn | secondsToHhMm }}
            </mat-cell>
          </ng-container>

          <ng-container matColumnDef="totalLoggedOut">
            <mat-header-cell *matHeaderCellDef mat-sort-header="totalLoggedOut">Total Logged Out</mat-header-cell>
            <mat-cell *matCellDef="let row">
              <span class="mobile-label">Total Logged Out:</span>
              {{ row.totalLoggedOut | secondsToHhMm }}
            </mat-cell>
          </ng-container>

          <ng-container matColumnDef="totalDuration">
            <mat-header-cell *matHeaderCellDef mat-sort-header="totalDuration">Total Duration</mat-header-cell>
            <mat-cell *matCellDef="let row">
              <span class="mobile-label">Total Duration:</span>
              {{ row.totalDuration | duration: true }}
            </mat-cell>
          </ng-container>

          <!-- Header and Row Declarations -->
          <mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></mat-header-row>
          <mat-row
            *matRowDef="let row; columns: displayedColumns"
            class="call-logs-row"
            [ngClass]="{ 'agent-logged-out': !row.loggedIn }"
          ></mat-row>
        </mat-table>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
        padding: 5px;
      }

      ::ng-deep .mat-mdc-row.agent-logged-out {
        .mat-mdc-cell {
          color: lightgrey !important;
        }
      }

      .mat-column-agentName {
        padding-left: 0 !important;
        max-width: 320px;
        min-width: 120px;
        white-space: nowrap;

        .mat-icon {
          min-width: 24px;
        }

        &.header-agent {
          padding-left: 16px !important;
        }

        .row-text {
          display: inline-block;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .mat-column-activeCall {
        max-width: 400px;
        min-width: 100px;
        white-space: nowrap;

        .row-text {
          display: inline-block;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .mat-column-totalLoggedIn,
      .mat-column-totalLoggedOut,
      .mat-column-totalDuration {
        max-width: 120px;
        min-width: 120px;
        display: flex;
        flex-direction: row;
        justify-content: center;
      }

      .mat-column-answeredCalls,
      .mat-column-missedCalls,
      .mat-column-totalCalls {
        max-width: 90px;
        min-width: 90px;
        display: flex;
        flex-direction: row;
        justify-content: center;
      }
    `,
  ],
  standalone: true,
  imports: [
    FlexModule,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    NgIf,
    MatIcon,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgClass,
    DurationPipe,
    SipContactPipe,
    SecondsToHhMmPipe,
    MatIconButton,
    MatTooltip,
    MatProgressSpinner,
    NgStyle,
  ],
})
export class WbQueueAgentsWidgetComponent implements OnChanges, AfterViewInit {
  @Input() itemData: WbDataQueueAgents;
  @Input() filterActive: boolean;
  @ViewChild(MatSort) sort: MatSort;

  dataSource = new MatTableDataSource<AgentsTableRow>([]);
  displayedColumns = [
    'agentName',
    'activeCall',
    'answeredCalls',
    'missedCalls',
    'totalCalls',
    'totalLoggedIn',
    'totalLoggedOut',
    'totalDuration',
  ];
  loadingAgentAccounts = new Set<string>();

  constructor(
    private pbxService: LocalPbxService,
    private notificationService: NotificationService,
  ) {}

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const activeCallsMap = new Map<string, PbxWbCurrentModel>();
    (this.itemData?.activeCalls || []).forEach(obj => {
      activeCallsMap.set(obj.agent, obj);
    });

    this.dataSource.data = (this.itemData?.agents || []).map(x => {
      const activeCall = activeCallsMap.get(x.account);
      let activeCallName = '';
      if (activeCall) {
        activeCallName = `${activeCall.from}`;
      }
      return {
        agentAccount: x.account,
        agentName: `${x.account} ${x.name}`,
        activeCall: activeCallName,
        loggedIn: x.login,
        totalCalls: x.calls + x.missed,
        answeredCalls: x.calls,
        missedCalls: x.missed,
        totalDuration: ((x.duration.talk || 0) + (x.duration.hold || 0)) * 1000,
        agentData: x,
        totalLoggedIn: x.totalLoggedIn,
        totalLoggedOut: x.totalLoggedOut,
      };
    });

    if (this.filterActive) {
      this.dataSource.data = this.dataSource.data.filter(x => x.loggedIn);
    }
  }

  async toggleAgentLogin(row: AgentsTableRow) {
    if (!this.itemData?.groupId || !row.agentAccount) return;
    if (this.loadingAgentAccounts.has(row.agentAccount)) return;
    this.loadingAgentAccounts.add(row.agentAccount);
    try {
      await this.pbxService.groupAgentLogin(this.itemData.groupId, row.agentAccount, !row.loggedIn);
      this.notificationService.notifySuccess(
        `Agent ${row.agentName} has been ${row.loggedIn ? 'logged out' : 'logged in'} successfully.`,
      );
      // Update the agent's loggedIn state in the dataSource
      const agent = this.dataSource.data.find(a => a.agentAccount === row.agentAccount);
      if (agent) {
        agent.loggedIn = !row.loggedIn;
      }
      this.dataSource.data = [...this.dataSource.data]; // trigger table update
    } catch (e) {
      this.notificationService.notifyDanger('Failed to update agent login status.');
    } finally {
      this.loadingAgentAccounts.delete(row.agentAccount);
    }
  }

  getAgentWorkInfo(account: string) {
    const agentData = this.itemData.agents.find(x => x.account === account);
    if (agentData) {
      const queueStartTime = DateTime.fromISO(agentData.work.begin);
      const queueEndTime = DateTime.fromISO(agentData.work.end);
      const totalWorkMinutes = queueEndTime.diff(queueStartTime, 'minutes');
      const now = DateTime.now();
      let lastLoginTime = queueStartTime;

      let totalLoggedOutMinutes = 0;
      if (agentData.work.segments.length > 0) {
        // logout periods
        for (const segment of agentData.work.segments) {
          const logoutStartTime = DateTime.fromISO(segment[0]);
          const logoutEndTime = DateTime.fromISO(segment[1]);
          lastLoginTime = logoutEndTime;
          const diff = logoutEndTime.diff(logoutStartTime, 'minutes');
          totalLoggedOutMinutes += diff.minutes;
        }
      }

      if (agentData.work.since) {
        const loggedOutSince = DateTime.fromISO(agentData.work.since);
        const diff = now.diff(loggedOutSince, 'minutes');
        totalLoggedOutMinutes += diff.minutes;
      }

      console.log(`Total Work Hours: ${totalWorkMinutes.minutes} | Total Logged Out Minutes: ${totalLoggedOutMinutes}`);

      let infoMessage = `Total Work Hours: ${this.getDurationStringFromMinutes(totalWorkMinutes.minutes)}\n`;
      infoMessage += `Total Logged Out Hours: ${this.getDurationStringFromMinutes(totalLoggedOutMinutes)}\n`;

      return infoMessage;
    }

    return 'No Work Information';
  }

  getDurationStringFromMinutes(durationInMinutes: number): string {
    if (!durationInMinutes) {
      return '00:00';
    }

    const hours = Math.floor(durationInMinutes / 60);
    const minutes = Math.floor(durationInMinutes - hours * 60);

    return `${pad(hours)}:${pad(minutes)}`;
  }

  get canToggleAgentLogin() {
    return (row: AgentsTableRow) => {
      return (
        this.itemData?.isManager === true ||
        (this.itemData?.userExtension && row.agentAccount === this.itemData.userExtension)
      );
    };
  }
}
